/**
 * Search input component that triggers the chat search modal.
 * 
 * This component provides a traditional search bar appearance that
 * opens the enhanced chat search modal when clicked or focused.
 */

import React, { useState, useRef } from 'react';

const SearchInput = React.forwardRef(({ onSearchClick, placeholder = "Search articles...", className = '' }, ref) => {
  const [justClosed, setJustClosed] = useState(false);
  const inputRef = useRef(null);

  const handleClick = () => {
    if (justClosed) {
      setJustClosed(false);
      return;
    }
    onSearchClick();
  };

  const handleFocus = () => {
    if (justClosed) {
      // Temporarily disable focus handler after modal close
      inputRef.current?.blur();
      setJustClosed(false);
      return;
    }
    onSearchClick();
  };

  const handleKeyDown = (e) => {
    // Open modal on any key press (except Tab)
    if (e.key !== 'Tab') {
      e.preventDefault();
      if (!justClosed) {
        onSearchClick();
      }
    }
  };

  // Expose method to parent to indicate modal was just closed
  React.useImperativeHandle(ref, () => ({
    notifyModalClosed: () => {
      setJustClosed(true);
      // Reset after a short delay
      setTimeout(() => setJustClosed(false), 200);
    }
  }));

  return (
    <div className={`relative ${className}`}>
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg 
          className="h-5 w-5 text-gray-400" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
          />
        </svg>
      </div>
      
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        onClick={handleClick}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        readOnly
        className={`
          block w-full pl-10 pr-3 py-2 
          border border-gray-300 rounded-md 
          leading-5 bg-white placeholder-gray-500 
          focus:outline-none focus:placeholder-gray-400 
          focus:ring-1 focus:ring-primary-500 focus:border-primary-500
          cursor-pointer hover:bg-gray-50 transition-colors
        `}
      />
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;